/**
 * KoApprovalModal (QGS v2)
 *
 * Line Leader signature dialog. Rendered inline from ChecklistPage when
 * the operator has picked KO on a defect row and filled every required
 * dropdown. Submits the KO to the backend's create-with-approval
 * endpoint; on success the row is already OPEN + APPROVED and the
 * batched flush on Next skips it.
 *
 * Props:
 *   isOpen           - bool, controls visibility
 *   onClose          - () => void, called on cancel / escape
 *   onApproved       - (backendKo) => void, called on 201 with the row payload
 *   itemLabel        - string, name of the check the operator raised KO on
 *   language         - 'en' | 'it'
 */
import React, { useEffect, useRef, useState } from 'react';

export default function KoApprovalModal({
  isOpen,
  onClose,
  onApproved,
  itemLabel,
  language = 'en',
  onSubmit, // async ({username, pin}) => backendKo|throws
}) {
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const pinRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setUsername('');
      setPin('');
      setSubmitting(false);
      setErrorMsg('');
      setAttemptsLeft(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const t = (en, it) => (language === 'it' ? it : en);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setErrorMsg('');
    if (!username.trim() || !pin.trim()) {
      setErrorMsg(t('Username and PIN are required.', "Nome utente e PIN obbligatori."));
      return;
    }
    setSubmitting(true);
    try {
      const backendKo = await onSubmit({ username: username.trim(), pin: pin.trim() });
      onApproved && onApproved(backendKo);
    } catch (err) {
      // err may carry {status, body} shape from our api helper
      const status = err.status;
      const body = err.body || {};
      if (status === 401) {
        const left = body.attempts_left;
        setAttemptsLeft(left);
        if (body.locked_now) {
          setErrorMsg(t(
            'This PIN is now locked. Ask an admin to reset.',
            'PIN bloccato. Chiedere all’amministratore di ripristinarlo.'
          ));
        } else {
          setErrorMsg(
            t('Incorrect PIN.', 'PIN errato.') +
            (typeof left === 'number' ? ' ' + t(`${left} attempts left.`, `${left} tentativi rimanenti.`) : '')
          );
        }
      } else if (status === 403) {
        setErrorMsg(t('User is not authorised to approve KOs.', 'Utente non autorizzato ad approvare KO.'));
      } else if (status === 423) {
        setErrorMsg(t(
          'PIN is locked for this user. Ask an admin to reset.',
          'PIN bloccato per l’utente. Chiedere il ripristino.'
        ));
      } else {
        setErrorMsg(err.message || t('Approval failed.', 'Approvazione fallita.'));
      }
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,34,55,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }}
    >
      <form
        onSubmit={handleSubmit}
        onClick={e => e.stopPropagation()}
        autoComplete="off"
        style={{
          background: '#fff', borderRadius: 12, width: 420, maxWidth: '100%',
          padding: 24, boxShadow: '0 10px 30px rgba(15,34,55,0.25)',
          display: 'flex', flexDirection: 'column', gap: 14,
        }}
      >
        {/* Decoy inputs. Chromium ignores autoComplete="off" on real login
            fields but will happily fill the FIRST username/password pair it
            finds. These hidden decoys absorb that autofill so the visible
            LL fields stay clean. */}
        <input type="text" name="username" autoComplete="username" tabIndex={-1}
               style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />
        <input type="password" name="password" autoComplete="current-password" tabIndex={-1}
               style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />
        <div>
          <h2 style={{ margin: 0, fontSize: 18, color: '#17253d' }}>
            {t('Line Leader approval', 'Approvazione Capo Linea')}
          </h2>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#4a596b' }}>
            {t(
              'Raising a KO requires a Line Leader signature.',
              'La creazione di un KO richiede la firma del Capo Linea.'
            )}
          </p>
        </div>

        {itemLabel && (
          <div style={{
            padding: 10, background: '#f4f7fa', borderRadius: 6,
            fontSize: 12, color: '#4a596b', wordBreak: 'break-word',
          }}>
            <strong style={{ color: '#17253d' }}>{t('Check', 'Verifica')}:</strong> {itemLabel}
          </div>
        )}

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#4a596b' }}>
            {t('Line Leader username', 'Nome utente Capo Linea')}
          </span>
          <input
            type="text"
            name="ll_approver_username"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-lpignore="true"
            data-form-type="other"
            autoFocus
            value={username}
            onChange={e => setUsername(e.target.value)}
            disabled={submitting}
            style={{
              padding: '10px 12px', border: '1px solid #cfd6de', borderRadius: 6,
              fontSize: 14, background: '#fff',
            }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#4a596b' }}>
            {t('PIN (4-6 digits)', 'PIN (4-6 cifre)')}
          </span>
          <input
            ref={pinRef}
            type="password"
            inputMode="numeric"
            name="ll_approver_pin"
            autoComplete="new-password"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-lpignore="true"
            data-form-type="other"
            maxLength={6}
            value={pin}
            onChange={e => setPin(e.target.value.replace(/[^\d]/g, ''))}
            disabled={submitting}
            style={{
              padding: '10px 12px', border: '1px solid #cfd6de', borderRadius: 6,
              fontSize: 20, letterSpacing: 8, textAlign: 'center', background: '#fff',
            }}
          />
        </label>

        {errorMsg && (
          <div style={{
            padding: '8px 10px', borderRadius: 6, background: '#fce8e6',
            border: '1px solid #f5b7b1', color: '#8a2c22', fontSize: 12,
          }}>{errorMsg}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '10px 16px', border: '1px solid #cfd6de', borderRadius: 6,
              background: '#fff', color: '#4a596b', fontSize: 13, fontWeight: 600,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >{t('Cancel', 'Annulla')}</button>
          <button
            type="submit"
            disabled={submitting}
            style={{
              padding: '10px 18px', border: 'none', borderRadius: 6,
              background: submitting ? '#8ab89f' : '#22a06b', color: '#fff',
              fontSize: 13, fontWeight: 700,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >{submitting ? t('Approving…', 'Approvazione…') : t('Approve', 'Approva')}</button>
        </div>
      </form>
    </div>
  );
}
